import React, { useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera, faSpinner, faImage } from "@fortawesome/free-solid-svg-icons";
import { psUpload } from "./PSApiClient";

/**
 * Compact banner/avatar uploader used on Circle and Chapter cards (director
 * only). Mirrors the working ImageUploader reference but talks directly to
 * psUpload and supports the `x-*-code` header multer's resolveFileName
 * needs to name the file deterministically.
 *
 * shape="banner" -> wide 16:5 cover art
 * shape="circle" -> round avatar
 */
export default function PSImageUploader({
  uploadUrl,
  codeHeader,
  code,
  currentUrl,
  shape = "banner",
  onUploaded,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const pick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");

    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image should be under 5 MB.");
      return;
    }
    if (!code) {
      setError("Save this first, then add a banner.");
      return;
    }

    setPreview(URL.createObjectURL(file));

    try {
      setUploading(true);
      const res = await psUpload(uploadUrl, file, { [codeHeader]: code });
      onUploaded?.(res?.url);
    } catch (err) {
      setError(err.message || "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const shapeClass = shape === "circle" ? "aspect-square rounded-full w-20" : "aspect-[16/5] w-full rounded-xl";
  const shown = preview || currentUrl;

  return (
    <div className="relative">
      <div
        onClick={pick}
        className={`group relative cursor-pointer overflow-hidden border border-white/15 bg-white/[0.04] ${shapeClass}`}
      >
        {shown ? (
          <img src={shown} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-white/35">
            <FontAwesomeIcon icon={faImage} className="text-lg" />
            <span className="text-[10px] font-medium">Add banner</span>
          </div>
        )}

        <div className="absolute inset-0 flex items-center justify-center gap-1.5 bg-black/0 text-[11px] font-semibold text-white opacity-0 transition group-hover:bg-black/40 group-hover:opacity-100">
          {uploading ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <>
              <FontAwesomeIcon icon={faCamera} />
              {shown ? "Change" : "Upload"}
            </>
          )}
        </div>
      </div>

      <input ref={inputRef} type="file" accept="image/*" hidden onChange={handleFile} />
      {error && <p className="mt-1 text-[11px] text-rose-300">{error}</p>}
    </div>
  );
}
